import { useEffect, useState, useMemo } from "react";
import clsx from "clsx";

// Imports => Constants
import { THEMES } from "@opp/ui-kit";

// Imports => Utilities
import { AcIsSet, AcIsArray, AcSanitize } from "@opp/ui-kit";

// Imports => Components
import { AcSearchInput, AcLoaderInline, AcRichContent } from "@opp/ui-kit";
import AcTransactionList from "@components/ac-transaction-list/ac-transaction-list";
import { withTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { withStore } from "../../stores";

const _CLASSES = {
  MAIN: "ac-search-box",
  BACKDROP: {
    MAIN: "ac-search-box__backdrop",
    VISIBLE: "ac-search-box__backdrop--visible",
    HAS_NOTICE: "ac-search-box__backdrop--has-notice",
  },
  RESULTS: {
    MAIN: "ac-search-box__results",
    HAS_NOTICE: "ac-search-box__results--has-notice",
  },
  INPUT: "ac-search-box__input",
};

const AcSearchBox = (props) => {
  const {
    store: { ui, global },
    name = "query",
    callback = () => {},
    reset = () => {},
    close = () => {},
    children,
    hasNotice,
    t,
  } = props;
  const [searchValue, setSearchValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [type, setType] = useState(null);

  const handleBackdropClick = (event) => {
    if (event && event.persist) {
      event.persist();
    }

    if (AcIsSet(close)) {
      close(event, name, searchValue);
    }
  };

  const handleKeyUp = (event) => {
    if (event.key === "Enter" || event.keyCode === 13) {
      if (callback && AcIsSet(callback)) {
        const val = AcSanitize(event.target.value);
        setSearchValue(val);
        callback(event, name, val, "transaction");
      }
    }
  };

  const handleCallback = (event, name, value) => {
    if (event && event.persist) {
      event.persist();
    }

    const val = AcSanitize(value.trim());
    if (!val) {
      setSearchValue("");
      handleClear(event);
    }

    const isValidUID = /[a-zA-Z]{3}_[0-9a-zA-Z]{12}/gi.test(val);

    // No valid UID
    if (!isValidUID) {
      return;
    }

    const arr = val.split("_");
    const prefix = arr[0];
    let typeOfUID = null;

    switch (prefix) {
      case "tra":
        typeOfUID = "transaction";
        break;
      case "wdr":
        typeOfUID = "withdrawal";
        break;
      case "ref":
        typeOfUID = "refund";
        break;
      case "trf":
        typeOfUID = "internal_transfer";
        break;

      // case 'set':
      //     typeOfUID = 'settlement';
      //     break;
      //
      // case 'mer':
      //     typeOfUID = 'merchant';
      //     break;
      //
      // case 'usr':
      //     typeOfUID = 'user';
      //     break;
      //
      default:
    }

    setType(typeOfUID);

    // Unrecognized form of UID
    if (!typeOfUID) {
      return;
    }

    if (callback && AcIsSet(callback)) {
      setSearchValue(val);
      callback(event, name, val, typeOfUID);
    }
  };

  const handleFocus = (event) => {
    setFocused(true);
  };
  const handleBlur = (event) => {
    setFocused(false);
  };

  const handleClear = (event) => {
    setFocused(false);
    setType(null);
    setSearchValue("");
    handleBackdropClick(event);
  };

  const hasResults = useMemo(() => {
    return (
      AcIsSet(global.current_search_results) &&
      AcIsArray(global.current_search_results)
    );
  }, [global.current_search_results]);

  const getInputClassNames = useMemo(() => {
    return clsx(_CLASSES.INPUT);
  }, []);

  const getResultsClassNames = useMemo(() => {
    return clsx(
      _CLASSES.RESULTS.MAIN,
      hasNotice && _CLASSES.RESULTS.HAS_NOTICE
    );
  }, [hasNotice]);

  const getBackdropClassNames = useMemo(() => {
    return clsx(
      _CLASSES.BACKDROP.MAIN,
      (focused || hasResults) && _CLASSES.BACKDROP.VISIBLE,
      hasNotice && _CLASSES.BACKDROP.HAS_NOTICE
    );
  }, [focused, hasResults, hasNotice]);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  }, []);

  const renderBackdrop = useMemo(() => {
    return (
      <div className={getBackdropClassNames} onClick={handleClear}>
        {global.is_loading && <AcLoaderInline theme={THEMES.PITCH} loading />}
      </div>
    );
  }, [global.is_loading, handleClear, getBackdropClassNames]);

  useEffect(() => {
    const $searchcolumn = document.querySelector(".ac-search-column");
    const $header = document.querySelector("#ac-header");

    const raiseTheRoof = focused || global.is_loading || hasResults;

    if (AcIsSet($searchcolumn)) {
      $searchcolumn.style.zIndex = raiseTheRoof ? "200010" : null;
    }

    if (AcIsSet($header)) {
      $header.style.zIndex = raiseTheRoof ? "200010" : null;
    }

    ui.setValue("backdrop", "visible", raiseTheRoof);
  }, [focused, global.is_loading, hasResults]);

  const renderField = useMemo(() => {
    return (
      <AcSearchInput
        name={name}
        value={searchValue}
        placeholder={`${t("global-search.find-by-uid", "Find payment by UID", {
          ns: "common",
        })}`}
        callback={handleCallback}
        onChange={handleCallback}
        onClear={handleClear}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyUp={handleKeyUp}
        className={getInputClassNames}
        additional={{
          autoCapitalize: "off",
          autoComplete: "off",
          autoCorrect: "off",
          autoSave: "myOppSearch",
          spellCheck: "false",
        }}
      />
    );
  }, [
    name,
    searchValue,
    handleFocus,
    handleBlur,
    handleClear,
    handleCallback,
    getInputClassNames,
  ]);

  const renderResults = useMemo(() => {
    if (!hasResults) {
      return null;
    } else if (global.is_loading) {
      return (
        <div className={getResultsClassNames}>
          <AcLoaderInline theme={THEMES.PITCH} loading />
        </div>
      );
    } else if (hasResults && global.current_search_results.length === 0) {
      return (
        <div className={getResultsClassNames}>
          <AcRichContent
            className={"h-text--center h-margin-y-25"}
            content={`<p>${t(
              "global-search.no-results",
              "We could not find any results for",
              { ns: "common" }
            )} "<strong>${searchValue}</strong>"</p>`}
          />
        </div>
      );
    }

    const num = global.current_search_results.length;

    // let Tag = AcTransactionList;
    //
    // switch (type) {
    //     case 'transaction':
    //         Tag = AcTransactionList;
    //         break;
    //     default:
    // }

    return (
      <div className={getResultsClassNames}>
        <AcRichContent
          className={"h-text--center h-margin-y-25"}
          content={`<p>${t("global-search.found-results", {
            count: num,
            ns: "common",
          })} "<strong>${searchValue}</strong>"</p>`}
        />
        <AcTransactionList items={global.current_search_results} />
      </div>
    );
  }, [
    searchValue,
    global.is_loading,
    type,
    hasResults,
    global.current_search_results,
    getResultsClassNames,
  ]);

  return (
    <div className={getMainClassNames} id={"ac-search-box"}>
      {renderBackdrop}
      {renderField}
      {renderResults}
    </div>
  );
};

export default withTranslation("common")(withStore(observer(AcSearchBox)));
